import { useEffect, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { getAppStore, setAppStore, useAppStore } from '@/store';
import runAutoGeneration from '@/utils/generation/runAutoGeneration';
import { getDeeplinkSnapshot, DeeplinkKind } from '@/features/deeplinks';
import { Screen } from '@/types';
import useAuth from './useAuth';

import type { IAppStore } from '@/store';

const selector = (state: IAppStore) => ({
  selectedPreset: state.selectedPreset,
  areCreditsAvailable: state.areCreditsAvailable,
  shouldSkipAutoGeneration: state.shouldSkipAutoGeneration,
  inputs: state.inputs,
  toolId: state.toolId,
  folderName: state.folderName,
  setGenerationError: state.setGenerationError,
  showToast: state.showToast,
});

const useAutoGeneration = () => {
  const { isAuthorized } = useAuth();
  const {
    selectedPreset,
    areCreditsAvailable,
    shouldSkipAutoGeneration,
    inputs,
    toolId,
    folderName,
    setGenerationError,
    showToast,
  } = useAppStore(useShallow(selector));

  const previousCreditsRef = useRef<boolean | null>(null);
  const autoGenerationInProgressRef = useRef(false);
  // Snapshot survives URL cleanup so the post-login resume still recognises
  // this as a deeplink session. The projectJsonHandler is responsible for
  // opening the registration modal at boot if unauthorized.
  const isDeeplinkSession =
    getDeeplinkSnapshot().kind === DeeplinkKind.ProjectJson;
  const [isAutoGenerationPending, setIsAutoGenerationPending] =
    useState(isDeeplinkSession);

  useEffect(() => {
    if (!isDeeplinkSession || shouldSkipAutoGeneration || !isAuthorized) {
      setIsAutoGenerationPending(false);
      return;
    }

    const { isPrimaryActionLoading } = getAppStore();
    const webhookId = selectedPreset?.flowId;

    const creditsBecameAvailable =
      previousCreditsRef.current === false && areCreditsAvailable === true;

    if (areCreditsAvailable !== null) {
      previousCreditsRef.current = areCreditsAvailable;
    }

    const shouldSkipAutoGenerationFlag =
      !webhookId || isPrimaryActionLoading || areCreditsAvailable === false;

    if (
      (shouldSkipAutoGenerationFlag && !creditsBecameAvailable) ||
      autoGenerationInProgressRef.current
    ) {
      setIsAutoGenerationPending(false);
      return;
    }

    // Guard: webhookId and selectedPreset are guaranteed to exist at this point
    if (!webhookId || !selectedPreset) return;

    autoGenerationInProgressRef.current = true;
    setIsAutoGenerationPending(false);

    runAutoGeneration({
      params: {
        webhookId,
        selectedPreset,
        inputs,
        toolId,
        folderName,
      },
      callbacks: {
        setGenerationError,
        showToast,
        onReset: () => {
          setAppStore({ screen: Screen.Create });
          setIsAutoGenerationPending(false);
          autoGenerationInProgressRef.current = false;
        },
        onPlaceholderCreated: () => {
          // Placeholder UUID is managed internally by runAutoGeneration
        },
      },
    });
  }, [
    areCreditsAvailable,
    isAuthorized,
    isDeeplinkSession,
    selectedPreset,
    inputs,
    toolId,
    folderName,
    shouldSkipAutoGeneration,
    setGenerationError,
    showToast,
  ]);

  return { canAutoGenerate: isAutoGenerationPending };
};

export default useAutoGeneration;
